'use client';

/**
 * Discovery Widget - Canvas View for Search Results
 *
 * @component_role Product Discovery Results Display
 * @business_rule Displays discovery search results in Grid/Graph/Table views.
 *   Supports product selection for Copilot details. Shows synthesis summary.
 *   Integrates with vendor and collection nodes from graph results.
 * @user_outcome Browse discovery results visually - switch views, click for details.
 * @integration_point Receives data from DiscoveryExplorer via tab store.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain discovery
 */

import { useState, useCallback, Component, type ReactNode, type ErrorInfo } from 'react';
import { LayoutGrid, Network, Table, Box, Grid2X2, Factory, Loader2, ShoppingCart, Check, Image as ImageIcon, Type, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useDiscoveryStore } from '@/lib/stores/discovery-store';
import { useTabStore } from '@/lib/stores/tab-store';
import { useTotalMatrixStore, useExplorerLabels } from '@/lib/stores/total-matrix-store';
import { useCartStore } from '@/lib/stores/cart-store';
import { DiscoveryGraphReagraph } from './discovery-graph-reagraph';
import type { DiscoveryResponse, DiscoverySearchResult, DiscoveryGraphNode, CollectionCard, LabelsDict } from '@/lib/api/types';

type ViewMode = 'grid' | 'graph' | 'table';

interface DiscoveryWidgetProps {
  entityId?: string;  // conversation_id (for canvas widget interface)
  title?: string;
  default_view?: string;
}

// Error boundary to catch render errors and show them instead of crashing
class DiscoveryErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[DiscoveryWidget] Render error:', error, info.componentStack);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-destructive p-4">
          <AlertTriangle className="w-8 h-8 mb-2" />
          <p className="font-medium">Discovery render error</p>
          <pre className="text-xs mt-2 max-w-full overflow-auto whitespace-pre-wrap">{this.state.error.message}</pre>
          <button onClick={() => this.setState({ error: null })} className="mt-3 text-sm underline">Try again</button>
        </div>
      );
    }
    return this.props.children;
  }
}

export function DiscoveryWidget(props: DiscoveryWidgetProps) {
  return (
    <DiscoveryErrorBoundary>
      <DiscoveryWidgetInner {...props} />
    </DiscoveryErrorBoundary>
  );
}

function DiscoveryWidgetInner({ entityId, title, default_view }: DiscoveryWidgetProps) {
  // Get data from discovery store — individual selectors to avoid cross-field render triggers
  const response = useDiscoveryStore((s) => s.response);
  const isLoading = useDiscoveryStore((s) => s.isLoading);
  const activeTabId = useTabStore((state) => state.activeTabId);
  const getViewportConfig = useTotalMatrixStore((state) => state.getViewportConfig);
  const L = useExplorerLabels('discovery');
  // default_view comes from matrix canvas_props
  const [viewMode, setViewMode] = useState<ViewMode>((default_view as ViewMode) ?? 'grid');
  const selectNode = useGraphSelectionStore((state) => state.selectNode);

  // Matrix-driven: check if we should show empty state on mode entry
  const viewportConfig = getViewportConfig('discovery', null);
  const emptyOnModeEntry = viewportConfig?.canvas?.props?.empty_on_mode_entry ?? false;

  // Show data only if: not empty_on_mode_entry OR there's an active discovery tab
  const hasActiveDiscoveryTab = activeTabId?.startsWith('discovery-') ?? false;
  const data = (emptyOnModeEntry && !hasActiveDiscoveryTab) ? null : response;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Box className="w-12 h-12 mb-3 opacity-50" />
        <p className="text-lg font-medium">{L.canvas_empty_heading}</p>
        <p className="text-sm">{L.canvas_empty_text}</p>
      </div>
    );
  }

  const products = data.products?.results || [];
  const collections = data.collections ?? [];
  const graphNodes = data.graph?.nodes || [];
  const graphEdges = data.graph?.edges || [];
  const isOutOfDomain = data.out_of_domain === true;

  const handleProductClick = (product: DiscoverySearchResult) => {
    selectNode({
      id: product.semantic_id,
      label: product.name,
      type: 'product',
      properties: product.properties,
    });
  };

  const handleNodeClick = (node: DiscoveryGraphNode) => {
    selectNode({
      id: node.id,
      label: node.label,
      type: node.type === 'Actor' ? 'vendor' : node.type.toLowerCase(),
      properties: node.properties,
    });
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header with View Toggle */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-panel-border bg-panel">
        <div>
          <h2 className="text-lg font-semibold">
            {isOutOfDomain ? L.ood_collections_header : L.products_label}
          </h2>
          <p className="text-sm text-muted-foreground">
            {isOutOfDomain
              ? (L.canvas_collections_count || '').replace('{n}', String(collections.length))
              : (L.canvas_products_found || '').replace('{n}', String(products.length)).replace('{turn}', String(data.turn))}
          </p>
        </div>

        {/* View Mode Toggle */}
        <div className="flex items-center gap-1 p-1 bg-muted rounded-lg">
          <button
            onClick={() => setViewMode('grid')}
            className={cn(
              'p-2 rounded transition-colors',
              viewMode === 'grid'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
            title={L.canvas_view_grid}
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('graph')}
            className={cn(
              'p-2 rounded transition-colors',
              viewMode === 'graph'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
            title={L.canvas_view_graph}
          >
            <Network className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={cn(
              'p-2 rounded transition-colors',
              viewMode === 'table'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
            title={L.canvas_view_table}
          >
            <Table className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className={cn(
        'flex-1',
        viewMode === 'graph' ? 'overflow-hidden' : 'overflow-auto p-4'
      )}>
        {viewMode === 'grid' && (
          <>
            {!isOutOfDomain && (
              <ProductGrid products={products} onProductClick={handleProductClick} L={L} />
            )}
            {collections.length > 0 && (
              <CollectionCards
                collections={collections}
                isOutOfDomain={isOutOfDomain}
                headerLabel={isOutOfDomain ? L.ood_collections_header : L.canvas_explore_collections}
                onCollectionClick={(sid, name) => {
                  selectNode({ id: sid, label: name, type: 'collection', properties: {} });
                }}
              />
            )}
          </>
        )}
        {viewMode === 'graph' && (
          <DiscoveryGraphReagraph
            nodes={graphNodes}
            edges={graphEdges}
            onNodeClick={handleNodeClick}
          />
        )}
        {viewMode === 'table' && (
          <TableView products={products} onProductClick={handleProductClick} L={L} />
        )}
      </div>
    </div>
  );
}

// ============================================================
// Union Match Indicator (Glass-Box Explainability)
// ============================================================

function UnionMatchIndicator({
  sourceIndex,
  sourceType,
}: {
  sourceIndex?: number;
  sourceType?: 'text' | 'image';
}) {
  // Only show for image matches (text is default, no indicator needed)
  if (sourceIndex === undefined || sourceType !== 'image') return null;

  return (
    <div
      className={cn(
        'absolute top-2 left-2 flex items-center gap-0.5',
        'bg-background/80 backdrop-blur-sm rounded px-1.5 py-0.5',
        'text-[10px] font-medium shadow-sm'
      )}
      title={`Matched image #${sourceIndex + 1}`}
    >
      <ImageIcon className="w-3 h-3 text-primary" />
      <span className="text-primary">{sourceIndex + 1}</span>
    </div>
  );
}

// ============================================================
// Grid View
// ============================================================

function ProductGrid({
  products,
  onProductClick,
  L,
}: {
  products: DiscoverySearchResult[];
  onProductClick: (product: DiscoverySearchResult) => void;
  L: LabelsDict;
}) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
      {products.map((product) => (
        <ProductCard
          key={product.semantic_id}
          product={product}
          onClick={() => onProductClick(product)}
          L={L}
        />
      ))}
    </div>
  );
}

// ============================================================
// Collection Cards
// ============================================================

function CollectionCards({
  collections,
  isOutOfDomain,
  headerLabel,
  onCollectionClick,
}: {
  collections: CollectionCard[];
  isOutOfDomain?: boolean;
  headerLabel?: string;
  onCollectionClick: (sid: string, name: string) => void;
}) {
  return (
    <div className={isOutOfDomain ? '' : 'mt-6 pt-4 border-t border-panel-border'}>
      {!isOutOfDomain && headerLabel && (
        <h3 className="text-sm font-medium text-muted-foreground mb-4">{headerLabel}</h3>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {collections.map((col) => (
          <button
            key={col.semantic_id}
            onClick={() => onCollectionClick(col.semantic_id, col.name)}
            className={cn(
              'group relative rounded-lg border border-panel-border overflow-hidden',
              'hover:border-primary/50 transition-colors text-left'
            )}
          >
            {/* Thumbnail */}
            <div className="aspect-square bg-muted relative">
              {col.thumbnail ? (
                <img
                  src={col.thumbnail}
                  alt={col.name}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Grid2X2 className="w-12 h-12 text-muted-foreground" />
                </div>
              )}
              {/* Product count badge */}
              <div className="absolute bottom-2 right-2 bg-background/80 backdrop-blur-sm px-2 py-0.5 rounded text-xs font-medium">
                {col.total_products}
              </div>
            </div>
            {/* Label */}
            <div className="p-3">
              <h3 className="font-medium text-sm truncate group-hover:text-primary transition-colors">
                {col.name}
              </h3>
              <p className="text-xs text-muted-foreground">{col.match_label}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ProductCard({
  product,
  onClick,
  L,
}: {
  product: DiscoverySearchResult;
  onClick: () => void;
  L: LabelsDict;
}) {
  const [justAdded, setJustAdded] = useState(false);
  const addItem = useCartStore((state) => state.addItem);
  const items = useCartStore((state) => state.items);

  const isInCart = items.some((item) => item.product_sid === product.semantic_id);
  const hasImage = !!product.properties.image_url;

  const handleAddToCart = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClick(); // Also select the product so Copilot shows details
      addItem({
        product_sid: product.semantic_id,
        name: product.name,
        quantity: 1,
        vendor_id: product.properties.vendor_sid || '',
        vendor_name: product.properties.vendor_name || '',
        list_price: product.properties.price,
        image_url: product.properties.image_url,
      });
      setJustAdded(true);
      setTimeout(() => setJustAdded(false), 1500);
    },
    [addItem, product, onClick]
  );

  return (
    <div
      onClick={onClick}
      className={cn(
        'group p-3 rounded-lg border border-panel-border bg-card cursor-pointer',
        'hover:border-primary/50 hover:shadow-md transition-all',
        'text-left flex flex-col'
      )}
    >
      {/* Image or Placeholder */}
      <div className="aspect-square rounded-md overflow-hidden bg-muted mb-3 relative">
        {hasImage ? (
          <img
            src={product.properties.image_url!}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Box className="w-8 h-8 text-muted-foreground/50" />
          </div>
        )}

        {/* Add to Cart — intent (RFQ / MCP / PO) is decided in the
            cart drawer at submit time, not here. Single button keeps
            the original "intent comes after selection" model coherent
            with edit flows (where intent is locked at edit-open). */}
        <button
          onClick={handleAddToCart}
          className={cn(
            'absolute top-2 right-2 p-1.5 rounded-md transition-all',
            'opacity-0 group-hover:opacity-100',
            justAdded
              ? 'bg-green-600 text-white'
              : isInCart
                ? 'bg-primary/80 text-primary-foreground'
                : 'bg-background/80 backdrop-blur-sm text-foreground hover:bg-primary hover:text-primary-foreground'
          )}
          title={isInCart ? L.canvas_add_more_to_rfq : L.canvas_add_to_rfq}
        >
          {justAdded ? (
            <Check className="w-4 h-4" />
          ) : (
            <ShoppingCart className="w-4 h-4" />
          )}
        </button>

        {/* In Cart Badge */}
        {isInCart && !justAdded && (
          <div className="absolute top-2 left-2 bg-primary text-primary-foreground text-[10px] px-1.5 py-0.5 rounded">
            {L.canvas_in_cart}
          </div>
        )}

        {/* Union Match Indicator - shows which image matched (for moodboard) */}
        {!isInCart && (
          <UnionMatchIndicator
            sourceIndex={product.matched_source_index}
            sourceType={product.matched_source_type}
          />
        )}
      </div>

      {/* Content */}
      <h3 className="font-medium text-sm line-clamp-2 mb-1">{product.name}</h3>

      {product.properties.vendor_name && (
        <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
          <Factory className="w-3 h-3" />
          {product.properties.vendor_name}
        </p>
      )}

      <div className="mt-auto flex items-center justify-between">
        {product.properties.price != null && (
          <span className="font-semibold text-sm">
            ${product.properties.price.toFixed(0)}
          </span>
        )}
        <span className="text-xs text-muted-foreground">
          {(product.similarity * 100).toFixed(0)}% match
        </span>
      </div>
    </div>
  );
}


// ============================================================
// Table View
// ============================================================

function TableView({
  products,
  onProductClick,
  L,
}: {
  products: DiscoverySearchResult[];
  onProductClick: (product: DiscoverySearchResult) => void;
  L: LabelsDict;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-panel-border">
            <th className="text-left py-2 px-3 font-medium">{L.canvas_table_product}</th>
            <th className="text-left py-2 px-3 font-medium">SKU</th>
            <th className="text-left py-2 px-3 font-medium">{L.canvas_table_vendor}</th>
            <th className="text-left py-2 px-3 font-medium">{L.canvas_table_category}</th>
            <th className="text-right py-2 px-3 font-medium">{L.canvas_table_price}</th>
            <th className="text-right py-2 px-3 font-medium">{L.canvas_table_match}</th>
            <th className="text-center py-2 px-3 font-medium">{L.canvas_table_source}</th>
          </tr>
        </thead>
        <tbody>
          {products.map((product) => (
            <tr
              key={product.semantic_id}
              onClick={() => onProductClick(product)}
              className="border-b border-panel-border/50 hover:bg-accent/30 cursor-pointer"
            >
              <td className="py-2 px-3">
                <div className="flex items-center gap-2">
                  {product.properties.image_url ? (
                    <img
                      src={product.properties.image_url}
                      alt=""
                      className="w-8 h-8 rounded object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                      <Box className="w-4 h-4 text-muted-foreground/50" />
                    </div>
                  )}
                  <span className="line-clamp-1">{product.name}</span>
                </div>
              </td>
              <td className="py-2 px-3 text-muted-foreground">
                {product.properties.sku || '-'}
              </td>
              <td className="py-2 px-3">
                {product.properties.vendor_name || '-'}
              </td>
              <td className="py-2 px-3 text-muted-foreground">
                {product.properties.category || '-'}
              </td>
              <td className="py-2 px-3 text-right font-medium">
                {product.properties.price != null
                  ? `$${product.properties.price.toFixed(0)}`
                  : '-'}
              </td>
              <td className="py-2 px-3 text-right text-muted-foreground">
                {(product.similarity * 100).toFixed(0)}%
              </td>
              <td className="py-2 px-3 text-center">
                {product.matched_source_type === 'image' ? (
                  <span className="inline-flex items-center gap-0.5 text-xs text-primary">
                    <ImageIcon className="w-3 h-3" />
                    {(product.matched_source_index ?? 0) + 1}
                  </span>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
